/**
 * SeatHandoff.tsx — WEB.4.SH (P0.1)
 *
 * Hot-seat handoff modal: full-screen opaque overlay shown between turns so
 * the new player can take the device without seeing the previous player's
 * private info (hand, etc.) leak.
 *
 * Opens automatically after `endTurn` when `seatHandoffEnabled` is true (and
 * game not ended and ≥2 active seats). Closes on Continue click OR Enter/Space.
 *
 * VISIBLE WHEN: store.seatHandoffOpen === true.
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { playSfx } from "../../audio/audio.js";
import { useI18n } from "../../i18n/useI18n.js";
import { SEAT_COLORS, useGameStore } from "../../store/game-store.js";
import styles from "./SeatHandoff.module.css";

export function SeatHandoff(): React.ReactElement | null {
  const open = useGameStore((s) => s.seatHandoffOpen);
  const fromSeat = useGameStore((s) => s.seatHandoffFromSeat);
  const toSeat = useGameStore((s) => s.currentSeat);
  const acknowledge = useGameStore((s) => s.acknowledgeSeatHandoff);
  const { t } = useI18n();

  // codex R1 Medium fix: focus Continue button when modal opens so keyboard
  // users / assistive tech land inside the modal instead of staying on the
  // (now-occluded) game controls underneath. Matches GameOverModal a11y level.
  const continueBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open) return;
    // P2.4: fire the seat-handoff chime on open (silent until audio files ship).
    playSfx("seat-handoff");
    // Focus on next frame so the dialog is mounted before the focus call.
    const id = window.setTimeout(() => {
      continueBtnRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [open]);

  // Keyboard: Enter or Space to acknowledge. Listener only active while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Enter" || e.key === " " || e.code === "Space") {
        e.preventDefault();
        acknowledge();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, acknowledge]);

  if (!open) return null;

  const toColor = SEAT_COLORS[toSeat] ?? SEAT_COLORS[0];
  const toLabel = `P${toSeat + 1}`;
  const fromLabel = fromSeat !== null ? `P${fromSeat + 1}` : null;

  // codex R1 Medium fix: <dialog open> gives native dialog semantics (better
  // than role="dialog" on a div per repo's biome `useSemanticElements`).
  // aria-labelledby points at the static "Pass device to" title.
  return (
    <dialog
      open
      className={styles.overlay}
      data-testid="seat-handoff-overlay"
      aria-labelledby="seat-handoff-title-text"
    >
      <div className={styles.panel}>
        {fromLabel !== null && (
          <div className={styles.subtitle} data-testid="seat-handoff-subtitle">
            {t("seatHandoff.turnEnded", { player: fromLabel })}
          </div>
        )}
        <div id="seat-handoff-title-text" className={styles.title} data-testid="seat-handoff-title">
          {t("seatHandoff.passDeviceTo")}
        </div>
        <div
          className={styles.seatBadge}
          style={{ background: toColor, borderColor: toColor }}
          data-testid="seat-handoff-badge"
        >
          {toLabel}
        </div>
        <button
          ref={continueBtnRef}
          type="button"
          className={styles.continueBtn}
          onClick={() => acknowledge()}
          data-testid="seat-handoff-continue"
        >
          {t("seatHandoff.continue")}
        </button>
        <div className={styles.hint} aria-hidden="true">
          {t("seatHandoff.pressEnterOrSpace")}
        </div>
      </div>
    </dialog>
  );
}
